import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useInspectionDetailStore } from '../store/Provider'
import { useShallow } from 'zustand/react/shallow'
import { useMemo } from 'react'
import {
  ExportInspectionFilesRequestBody,
  DEFAULT_SEARCH_PARAMS_ACCESS_TOKEN_STRATEGY,
  InspectionDetailIframeRequestQuery,
  ListInspectionEventsResponseBody,
} from 'bff'
import { InspectionDetailTrackingEvent } from '@detail/store/factory'
import {
  PRESIGN_AUTHENTICATION_HEADER_KEY,
  PresignResourceType,
} from 'ai-constants'
import { UpdateTemplateLogicRuleRequestBody } from '@detail/components/InspectionDetail/LogicRules/Form'

if (!process.env.NEXT_PUBLIC_API_URL)
  throw new Error('NEXT_PUBLIC_API_URL is not defined')

const UNAUTHORIZED_CODE = 401

const httpClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  withCredentials: true,
})

export const injectOAuthParams = (
  request: AxiosRequestConfig,
  searchParams: URLSearchParams,
) => {
  const accessToken = searchParams.get(
    'accessToken' satisfies keyof InspectionDetailIframeRequestQuery,
  )
  const strategy: InspectionDetailIframeRequestQuery['strategy'] =
    searchParams.get(
      'strategy' satisfies keyof InspectionDetailIframeRequestQuery,
    ) ?? DEFAULT_SEARCH_PARAMS_ACCESS_TOKEN_STRATEGY

  if (accessToken && request.headers && strategy === 'oauth') {
    request.headers['Authorization'] = `Bearer ${accessToken}`
  }

  if (accessToken && request.headers && strategy === 'presign') {
    request.headers[PRESIGN_AUTHENTICATION_HEADER_KEY] = accessToken
  }
}

httpClient.interceptors.request.use(
  (request) => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      injectOAuthParams(request, searchParams)
    }

    return request
  },
  (error) => {
    console.log('error', error)
    return Promise.reject(error)
  },
)

httpClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error: Error | AxiosError) => {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === UNAUTHORIZED_CODE) {
        if (typeof window !== 'undefined' && 'Intercom' in window) {
          window.Intercom('shutdown')
        }
      }
    }
    return Promise.reject(error)
  },
)

export type InspectionDetailApiClient = ReturnType<typeof useApiClient>

export interface PatchCompleteStageAssetsRequestBody {
  assets: Array<{
    identifier: string
    type: string
    value: unknown
  }>
}

const trackingEventsFetcherByPlatform = {
  dashboard: (inspectionId: string) =>
    httpClient
      .get<ListInspectionEventsResponseBody>(
        `/v1/inspection/${inspectionId}/events`,
      )
      .then((response) => response.data.events || []),
  office: (inspectionId: string) =>
    httpClient
      .get<InspectionDetailTrackingEvent[]>(
        `/v1/tracking/office/events/inspection/${inspectionId}`,
      )
      .then((response) => response.data),
} as const

export const useApiClient = () => {
  const inspection = useInspectionDetailStore(
    useShallow((state) => ({
      _id: state._id,
      type: state.type,
      platform: state.platform,
    })),
  )

  const client = useMemo(() => {
    return {
      downloadAuditTrail: () =>
        httpClient
          .get<{
            url: string
          }>(`/v1/inspection/${inspection._id}/events/pdf`)
          .then((res) => res.data),
      downloadInspectionReport: () =>
        httpClient
          .get<{
            url: string
          }>(`/v1/inspection/${inspection.type}/pdf/file/${inspection._id}`)
          .then((res) => res.data),
      exportInspectionFiles: ({
        body,
      }: {
        body: ExportInspectionFilesRequestBody
      }) =>
        httpClient
          .post(`/v1/inspection/files/export/${inspection._id}`, body)
          .then((res) => res.data),
      generateInputValueFileURL: (data: {
        identifier: string
        productInspectionItemId?: string
        resource: PresignResourceType
      }) =>
        httpClient
          .get<{ url: string; notFound: boolean }>(
            `/v1/inspection/${inspection._id}/input/file/url`,
            {
              params: {
                identifier: data.identifier,
                productInspectionItemId: data.productInspectionItemId,
                resource: data.resource,
              },
            },
          )
          .then((res) => res.data),
      patchCompleteStageAssets: ({
        body,
      }: {
        body: PatchCompleteStageAssetsRequestBody
      }) =>
        httpClient
          .patch(`/v1/inspection/${inspection._id}/assets/complete`, body)
          .then((res) => res.data),
      listInspectionTrackingEvents: () =>
        trackingEventsFetcherByPlatform[inspection.platform](inspection._id),
    }
  }, [inspection])

  return client
}
